import { useState, useEffect } from 'react';
import { Tag, Tooltip } from 'antd';
import { useApi } from '../../hooks/use_api';
import { useTheme } from '../../context/ThemeContext';
import { EntityImageById } from './EntityAvatar';
import { EntityImage } from './EntityImage';

/**
 * Resolves a message sender to an avatar. Three cases, in priority order:
 *   1. Vellby — the assistant or a system message ("Vellby" with no sender_id):
 *      render the product logo (optionally with a label).
 *   2. A known person — sender_id (Keycloak UUID) resolves to a person entity:
 *      render that entity's round picture (uploaded or generated).
 *   3. Unknown — a generated picture seeded by the sender's name.
 * People get just the round picture, with their name on hover.
 *
 * The UUID→entity lookup is memoised process-wide so the same sender is never
 * fetched twice across the whole app (both chat surfaces share this cache).
 */

// Keycloak user UUID → numeric entity ID (or null if not found)
const userEntityCache = {};

const AVATAR_SIZE = 20;

const TAG_STYLE = {
    fontWeight: 700,
    fontSize: 10,
    padding: '1px 5px',
    borderRadius: 4,
    margin: 0,
    lineHeight: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
};

export default function SenderAvatar({ senderId, senderName, role, showLabel = false }) {
    const { isDarkMode } = useTheme();
    const api = useApi();
    const isVellby = role === 'assistant' || (!senderId && senderName === 'Vellby');
    const [entityId, setEntityId] = useState(() => userEntityCache[senderId] ?? undefined);

    useEffect(() => {
        if (isVellby || !senderId || entityId !== undefined) return;
        api.get('/api/entities', { params: { user_id: senderId, entity_type: 'person' } })
            .then(data => {
                const id = data?.[0]?.id ?? null;
                userEntityCache[senderId] = id;
                setEntityId(id);
            })
            .catch(() => { userEntityCache[senderId] = null; setEntityId(null); });
    }, [senderId, entityId, isVellby]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isVellby) {
        const vellbyLogoSrc = isDarkMode ? '/white_outline.svg' : '/black_outline.svg';
        return (
            <Tag style={TAG_STYLE}>
                <img src={vellbyLogoSrc} alt="" style={{ width: 12, height: 12 }} />
                {showLabel && <span className="vellby-label">Vellby</span>}
            </Tag>
        );
    }

    let picture;
    if (entityId) {
        picture = <EntityImageById entityId={entityId} size={AVATAR_SIZE} />;
    } else if (entityId === undefined && senderId) {
        // Still resolving: a blank circle, so a generated picture doesn't flash
        // before the real one arrives.
        picture = <span style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: '50%', background: 'var(--bg-secondary)', flexShrink: 0 }} />;
    } else {
        picture = <EntityImage entity={{ entity_type: 'person', avatar: { seed: `sender:${senderName || senderId || '?'}` } }} size={AVATAR_SIZE} />;
    }
    return (
        <Tooltip title={senderName}>
            <span style={{ display: 'inline-flex', flexShrink: 0 }}>{picture}</span>
        </Tooltip>
    );
}
